/**
 * UploadReadinessScorecard - Org-wide CYBER Upload Readiness overview.
 * Runs deterministic Smart Assist pre-checks across every doc in the current queue
 * (no LLM calls — fast, free). Surfaces worst-scoring docs and youths at risk so
 * supervisors can triage before the nightly CYBER push.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  ShieldCheck, AlertTriangle, Sparkles, RefreshCw, ChevronDown, ChevronUp,
  TrendingUp, Users, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const scoreColor = (s) =>
  s >= 80 ? 'text-emerald-600' :
  s >= 50 ? 'text-amber-600' :
            'text-red-600';

const scoreRingColor = (s) =>
  s >= 80 ? '#10b981' :
  s >= 50 ? '#f59e0b' :
            '#ef4444';

const ScoreRing = ({ score }) => (
  <div className="relative w-16 h-16 flex-shrink-0">
    <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
      <circle cx="18" cy="18" r="16" fill="none" stroke="#e5e7eb" strokeWidth="3" />
      <circle
        cx="18" cy="18" r="16" fill="none"
        stroke={scoreRingColor(score)}
        strokeWidth="3"
        strokeDasharray={`${score}, 100`}
        strokeLinecap="round"
      />
    </svg>
    <div className="absolute inset-0 flex items-center justify-center">
      <span className={`text-sm font-bold ${scoreColor(score)}`}>{score}</span>
    </div>
  </div>
);

export const UploadReadinessScorecard = ({ token, onOpenSmartAssist }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const fetchScorecard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API_URL}/api/performcare/upload-center/readiness-scorecard?limit=15`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success) {
        setData(res.data);
      } else {
        setError('Failed to load scorecard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchScorecard();
  }, [fetchScorecard]);

  if (loading && !data) {
    return (
      <Card className="border-sky-200 bg-sky-50/30" data-testid="readiness-scorecard-loading">
        <CardContent className="flex items-center gap-3 p-4">
          <Loader2 className="w-5 h-5 animate-spin text-sky-600" />
          <span className="text-sm text-gray-600">Calculating CYBER upload readiness…</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50/50">
        <CardContent className="flex items-center justify-between p-4">
          <div className="flex items-center gap-2 text-sm text-red-700">
            <AlertTriangle className="w-4 h-4" /> Readiness Scorecard unavailable: {error}
          </div>
          <Button size="sm" variant="outline" onClick={fetchScorecard}>Retry</Button>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { totals, worst_documents = [], youths_at_risk = [] } = data;
  const dist = totals?.distribution || { green: 0, amber: 0, red: 0 };
  const docCount = totals?.documents || 0;

  // Empty state — nothing in queue
  if (docCount === 0) {
    return (
      <Card className="border-emerald-200 bg-emerald-50/40" data-testid="readiness-scorecard-empty">
        <CardContent className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-6 h-6 text-emerald-600" />
            <div>
              <p className="font-semibold text-emerald-800">Upload Readiness Scorecard</p>
              <p className="text-xs text-emerald-700">Nothing queued — your CYBER upload pipeline is clear.</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchScorecard}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-sky-200" data-testid="readiness-scorecard">
      <CardContent className="p-4">
        {/* Header row */}
        <div className="flex items-center gap-4 flex-wrap">
          <ScoreRing score={Math.round(totals.average_score || 0)} />

          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-violet-500" />
              <h3 className="font-semibold text-gray-900">Upload Readiness Scorecard</h3>
              <Badge variant="outline" className="text-xs">org-wide</Badge>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              {docCount} doc{docCount === 1 ? '' : 's'} · avg {totals.average_score}/100 ·
              {' '}{totals.total_blockers} blocker{totals.total_blockers === 1 ? '' : 's'} ·
              {' '}{totals.total_warnings} warning{totals.total_warnings === 1 ? '' : 's'} ·
              {' '}{totals.youths_at_risk} youth{totals.youths_at_risk === 1 ? '' : 's'} at risk
            </p>
          </div>

          {/* Distribution pills */}
          <div className="flex items-center gap-1.5" data-testid="readiness-distribution">
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100">
              {dist.green} green
            </Badge>
            <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100">
              {dist.amber} amber
            </Badge>
            <Badge className="bg-red-100 text-red-800 border-red-200 hover:bg-red-100">
              {dist.red} red
            </Badge>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchScorecard}
              disabled={loading}
              title="Refresh scorecard"
              data-testid="readiness-scorecard-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              data-testid="readiness-scorecard-toggle"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Distribution bar */}
        <div className="mt-3 w-full h-1.5 bg-gray-100 rounded-full overflow-hidden flex">
          {dist.green > 0 && (
            <div className="bg-emerald-500" style={{ width: `${(dist.green / docCount) * 100}%` }} />
          )}
          {dist.amber > 0 && (
            <div className="bg-amber-500" style={{ width: `${(dist.amber / docCount) * 100}%` }} />
          )}
          {dist.red > 0 && (
            <div className="bg-red-500" style={{ width: `${(dist.red / docCount) * 100}%` }} />
          )}
        </div>

        {/* Expanded detail */}
        {expanded && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4 pt-4 border-t">
            {/* Worst-scoring docs */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-red-500" />
                <h4 className="text-sm font-semibold text-gray-700">Lowest-scoring documents</h4>
              </div>
              {worst_documents.length === 0 ? (
                <p className="text-xs text-gray-400">No issues found — everything is green.</p>
              ) : (
                <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid="readiness-worst-docs">
                  {worst_documents.map((d) => (
                    <div
                      key={d.doc_id}
                      className="flex items-center gap-2 p-2 bg-gray-50 rounded border text-xs"
                      data-testid={`readiness-worst-${d.doc_id}`}
                    >
                      <span className={`font-bold w-8 text-right ${scoreColor(d.score)}`}>{d.score}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 truncate">
                          <span className="font-medium text-gray-800">{d.client_name}</span>
                          <span className="text-gray-400">·</span>
                          <span className="capitalize text-gray-500">
                            {d.doc_type.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <div className="text-gray-500 truncate">{d.top_issue || '—'}</div>
                      </div>
                      {d.blockers > 0 && (
                        <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px]">
                          {d.blockers}B
                        </Badge>
                      )}
                      {d.warnings > 0 && (
                        <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">
                          {d.warnings}W
                        </Badge>
                      )}
                      {onOpenSmartAssist && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 text-violet-600"
                          onClick={() => onOpenSmartAssist(d)}
                          title="Open Smart Assist"
                          data-testid={`readiness-open-assist-${d.doc_id}`}
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Youths at risk */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-amber-500" />
                <h4 className="text-sm font-semibold text-gray-700">Youths at risk</h4>
              </div>
              {youths_at_risk.length === 0 ? (
                <p className="text-xs text-gray-400">No youths flagged.</p>
              ) : (
                <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid="readiness-youths">
                  {youths_at_risk.map((y) => (
                    <div
                      key={y.client_id}
                      className="flex items-center gap-2 p-2 bg-gray-50 rounded border text-xs"
                    >
                      <span className={`font-bold w-8 text-right ${scoreColor(y.min_score)}`}>
                        {y.min_score}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-800 truncate">
                          {y.client_name}
                          {y.cyber_id && (
                            <span className="text-gray-400 font-normal ml-1">· CYBER {y.cyber_id}</span>
                          )}
                        </div>
                        <div className="text-gray-500">
                          {y.doc_count} doc{y.doc_count === 1 ? '' : 's'} ·
                          {' '}{y.blockers} blocker{y.blockers === 1 ? '' : 's'} ·
                          {' '}{y.warnings} warning{y.warnings === 1 ? '' : 's'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UploadReadinessScorecard;
